import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { ENDPOINTS } from '@philips-emr/hpms-health-insurance-commons';
import { catchRenderException } from '@philips-emr/hpms-health-insurance-components';
import { DeckAPI, FieldAPI, PanelAPI, PDFViewerAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnDynamicContentRender, OnMount, OnSelectItem } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';

import { TechnicalVisitDetailsAliases as Aliases, FragmentNames } from '../../codegen';
import { ROUTE } from '../../route';
import BaseController from '../BaseController';

interface TechnicalVisitDetails {
  ieStatus: number,
  dsStatus: string,
  nrSeqForm: number,
}

interface TechnicalVisitForm {
  fillOutDate: string,
}

@Controller({ fragment: FragmentNames.TECHNICAL_VISIT_DETAILS })
export default class TechnicalVisitController extends BaseController {

  @Inject(Aliases.PANEL_ESTABLISHMENT_DATA)
  private panelEstablishmentData: PanelAPI;

  @OnMount(Aliases.PANEL_MAIN)
  onMount(): void {
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqVisit });

    void HttpService.get(ENDPOINTS.GET.TECHNICAL_VISIT_DETAILS_REPORT, { nrSeqVisit })
      .then((resp) => {
        if (resp.status === 200) {
          catchRenderException(() => {
            this.panelEstablishmentData.setVisible(true);
          });
        }
      });
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD_VISIT_FORM)
  onDynamicContentRender(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as unknown as TechnicalVisitDetails;

    const stamp = this.getStampForm(ieStatus, dsStatus);

    if(stamp) {
      render((): ReactElement => stamp);
    }
  }

  @OnMount(Aliases.PDF_VIEWER)
  onMountPdfViewer(event: MountEvent): void {
    const pdfViewer = event.target as PDFViewerAPI;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();

    pdfViewer.setSrc(ENDPOINTS.GET.TECHNICAL_VISIT_DETAILS_REPORT.replace(':nrSeqVisit', nrSeqVisit as string));
  }

  @OnSelectItem(Aliases.DECK_FORMS)
  onSelectItem(event: SelectItemEvent<TechnicalVisitDetails>): void {
    const { nrSeqForm } = event.item;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    const api = event.target as unknown as DeckAPI;

    api.unselectAll();
    this.historyService.push(ROUTE.TECHNICAL_VISIT_FORMS, { nrSeqVisit, nrSeqForm });
  }

  @OnMount(Aliases.FIELD_FILLING_DATE)
  onMountFieldDate({ target }: MountEvent): void {
    const api = target as FieldAPI;
    const { fillOutDate } = api.getData() as unknown as TechnicalVisitForm;

    api.setVisible(!!fillOutDate);

  }

  private getStampForm(ieStatus: number, dsStatus: string): ReactElement | null {
    let stampProps = null;
    switch(ieStatus) {
      case 0:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label: dsStatus };
        break;
      case 1:
        stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.WARNING, label: dsStatus };
        break;
      case 2:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED, label: dsStatus };
        break;
      case 3:
        stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label: dsStatus };
        break;
      default:
        return null;
    }
    return <Stamp {...stampProps} />;
  }
}
